import { CloseSVG, JobStory, mqDown, useJobList, useJobStory } from 'rwl-ui';
import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Header } from '../../Layout/Header/Header';
import { Footer } from '../../Layout/Footer/Footer';
import { CTABar } from '../CTABar';
import { CloseButton } from '../../CloseButton';

import { closeSidebar } from '../../Layout/utils';
import { useModalStack } from '../../hooks/useModalStack';
import { useEnqueueEngagement, useGlobal } from '../../hooks';
import { useFetchJobDetails } from '../../fetchers/JobFetcher/useFetchJobDetails';
import { ACTION_TYPE } from 'rwl-shared/dist/models/EngagementEvent';
import { JobStoryModalPortal } from './JobStoryModalPortal';

const ModalContainer = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 0;
    bottom: 0;
    width: 80%;
    max-width: 900px;

    ${mqDown('md')} {
        width: 100% !important;
        overflow: auto;
    }

    background-color: var(--rwl-background-primary);

    /* Elevation 6 */
    box-shadow: 0px 1px 18px 0px rgba(0, 0, 0, 0.12),
        0px 6px 10px 0px rgba(0, 0, 0, 0.14),
        0px 3px 5px -1px rgba(0, 0, 0, 0.2);

    display: flex;
    flex-direction: column;
`;

const JobStoryContainer = styled.div`
    display: flex;
    overflow: auto;
    flex: 1;
`;

export const JobStoryModal = () => {
    const { accountId, config } = useGlobal();
    const { jobStoryId, setJobStoryId, setSimilarJobs, clearJobStory } =
        useJobStory();
    const { setOnJobCardClickHandler } = useJobList();
    const { data: details } = useFetchJobDetails({
        jobId: jobStoryId,
        accountId,
        showcaseOnly: config?.extraData?.showcaseOnly,
    });
    const { registerModal, unregisterModal } = useModalStack();
    const { enqueue } = useEnqueueEngagement();
    const containerRef = useRef<HTMLDivElement>(null);

    const handleOnClose = () => {
        clearJobStory();
    };

    useEffect(() => {
        if (jobStoryId) {
            registerModal('job-story-modal', handleOnClose);
            setSimilarJobs(details?.similarJobs || []);
            enqueue({
                actionType: ACTION_TYPE.VIEW,
                type: 'Engagement',
                target: 'Job Story',
                workSiteId: jobStoryId,
            });
        } else {
            unregisterModal('job-story-modal');
        }
    }, [jobStoryId, details]);

    useEffect(() => {
        const clickJobClickHandler = function (jobId: string) {
            setJobStoryId(jobId);
            closeSidebar();
        };

        setOnJobCardClickHandler(clickJobClickHandler);
    }, []);

    // Track time spent viewing the story
    useEffect(() => {
        let startTime: number | undefined;
        const captureTime = () => {
            if (!startTime) {
                return;
            }

            const endTime = new Date().getTime();
            const duration = (endTime - startTime) / 1000;

            enqueue({
                actionType: ACTION_TYPE.TIMER,
                type: 'Engagement',
                target: 'Time Spent on Job Story',
                workSiteId: jobStoryId,
                duration,
            });
        };

        if (jobStoryId) {
            startTime = new Date().getTime();
            window.addEventListener('beforeunload', captureTime);
        }
        return () => {
            captureTime();
            window.removeEventListener('beforeunload', captureTime);
        };
    }, [jobStoryId]);

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
            }}
            ref={containerRef}
        >
            <JobStoryModalPortal
                onClose={handleOnClose}
                isOpen={!!jobStoryId}
                containerRef={containerRef}
            >
                <ModalContainer role="dialog">
                    <Header hideAggregates>
                        <CloseButton onClick={() => handleOnClose()}>
                            <CloseSVG
                                style={{
                                    width: '3em',
                                }}
                            />
                        </CloseButton>
                    </Header>
                    <JobStoryContainer>
                        <JobStory job={details?.job} />
                    </JobStoryContainer>
                    <Footer>
                        <div />
                        <CTABar workSiteId={jobStoryId} />
                    </Footer>
                </ModalContainer>
            </JobStoryModalPortal>
        </div>
    );
};
